import React, {useEffect, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField} from "@mui/material";
import {toast} from "react-toastify";
import {AmiRepository} from "../../../../repositories/AmiRepository";
import {varsListToObject, VariablesEditor} from "./VariablesEditor";
import {DEFAULT_CONTEXT, DEFAULT_TRUNK, ExtensionAdvancedSettings} from "./ExtensionAdvancedSettings";
import {DEFAULT_VERSION_TYPE, LATEST_VERSION, SubProjectFields, toVariables} from "./SubProjectFields";

// subProjects (registry projects): the extension is created in projectId, owned by the selected language
// project and running its flow; defaultSubProjectId is preselected
export const AddExtensionModal = ({
                                    open, projectId, flowUid: pageFlowUid, subProjects, defaultSubProjectId,
                                    handleClose, onCreated
                                  }) => {
  const [extensionNumber, setExtensionNumber] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [trunk, setTrunk] = useState(DEFAULT_TRUNK);
  const [context, setContext] = useState(DEFAULT_CONTEXT);
  const [varRows, setVarRows] = useState([]);
  const [target, setTarget] = useState({subProjectId: null, versionType: DEFAULT_VERSION_TYPE, version: LATEST_VERSION});
  const [loading, setLoading] = useState(false);

  const hasSubProjects = !!subProjects;
  const flowUid = hasSubProjects
    ? subProjects.find(item => item.id === target.subProjectId)?.flowUid
    : pageFlowUid;

  useEffect(() => {
    if (open) {
      setExtensionNumber("");
      setName("");
      setDescription("");
      setTrunk(DEFAULT_TRUNK);
      setContext(DEFAULT_CONTEXT);
      setTarget({
        subProjectId: defaultSubProjectId ?? null,
        versionType: DEFAULT_VERSION_TYPE,
        version: LATEST_VERSION,
      });
      setVarRows([
        // set from the sub project selection when there is one
        ...(hasSubProjects ? [] : [{key: "FLOW_UID", value: pageFlowUid || ""}]),
        {key: "AUDIOSOCKET_HOST", value: "127.0.0.1:9099"},
        {key: "BEEP_SIGNAL", value: "false"},
        {key: "SEGMENT", value: "prepaid"},
      ]);
    }
  }, [open, pageFlowUid, defaultSubProjectId, hasSubProjects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!flowUid) return;
    setLoading(true);
    try {
      const response = await AmiRepository.registerExtension(projectId || flowUid, {
        extension: extensionNumber.trim() || undefined,
        sub_project_id: hasSubProjects ? String(target.subProjectId) : undefined,
        flow_uid: flowUid,
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        trunk: trunk.trim() || DEFAULT_TRUNK,
        context: context.trim() || DEFAULT_CONTEXT,
        variables: subProjects ? toVariables(varRows, target, subProjects, {}, projectId) : varsListToObject(varRows),
      });
      toast.success(`Extension ${response?.data?.extension || ""} created`);
      if (response?.data?.conf_regenerated === false) {
        toast.error(response?.data?.conf_error || "Configuration file failed to regenerate");
      }
      onCreated?.(response?.data);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to create extension");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
        sx: {minWidth: "360px", maxWidth: "sm", borderRadius: 4, p: 2, width: "100%"},
      }}
    >
      <DialogTitle>Add Extension</DialogTitle>
      <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, py: "16px !important"}}>
        <TextField
          label="Extension Number" fullWidth
          value={extensionNumber} onChange={(e) => setExtensionNumber(e.target.value)}
          placeholder="e.g. 950002"
          helperText="Leave empty to auto-assign the next free slot in the sub project's block"
        />
        <TextField
          label="Name" fullWidth
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Support bot"
        />
        <TextField
          label="Description" fullWidth
          value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Handles incoming support calls"
        />
        <ExtensionAdvancedSettings
          trunk={trunk} context={context}
          onTrunkChange={setTrunk} onContextChange={setContext}
        />
        {subProjects && (
          <SubProjectFields subProjects={subProjects} target={target} onChange={setTarget} required/>
        )}
        <VariablesEditor rows={varRows} onChange={setVarRows}/>

      </DialogContent>
      <DialogActions sx={{px: 2, py: 1}}>
        <Button variant="ghost" onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button type="submit" variant="modal" disabled={loading}>
          {loading ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
